import { Link } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { VyneCheckbox, VyneSelect } from '@vynedental/design-system'
import { CheckboxChangeEvent } from 'antd'

import VyneTooltip from '../../../components/VyneTooltip/VyneTooltip'
import { PracticeLocationReschedulingConfiguration } from '../../../models/v2/Practice'

import {
    coercePriorValueForUnit,
    CONCURRENCY_NUM_ARRAY,
    DAYS_PRIOR_VALUES,
    HOURS_PRIOR_VALUES,
    PRIOR_UNIT_VALUES,
    RESCHEDULING_VALUES,
} from './constants'

const moduleName = 'appointment-settings-locations-list'

type Props = {
    configuration: PracticeLocationReschedulingConfiguration
    onUpdate: (configuration: PracticeLocationReschedulingConfiguration) => void
    disabled: boolean
    isOnlineSchedulingWritebackEnabled: boolean
    practiceId: string
    isDentrixAgent: boolean
}

const sidebookingValues = CONCURRENCY_NUM_ARRAY.map(num => ({ value: num.toString(), display: `${num}` }))

const ReschedulingFields = ({
    configuration,
    onUpdate,
    disabled,
    isOnlineSchedulingWritebackEnabled,
    practiceId,
    isDentrixAgent,
}: Props) => {
    const showWritebackTooltip = !isOnlineSchedulingWritebackEnabled
    const priorValues = configuration.reschedule_prior_unit === 'hours' ? HOURS_PRIOR_VALUES : DAYS_PRIOR_VALUES
    const reschedulingPolicyOptions = RESCHEDULING_VALUES.map(({ value, display }) => ({
        value,
        label: display,
        disabled: value === 'allow' && !isOnlineSchedulingWritebackEnabled,
    }))
    const priorUnitOptions = PRIOR_UNIT_VALUES.map(({ value, display }) => ({ value, label: display }))
    const priorOptions = priorValues.map(({ value, display }) => ({ value, label: display }))
    const sidebookingOptions = sidebookingValues.map(({ value, display }) => ({ value, label: display }))

    return (
        <>
            <Grid item={true} xs={6} sm={3} md={4} lg={2.3} xl={2.3} className={`${moduleName}__table-item details`}>
                <label className={`${moduleName}__input-label`}>
                    Reschedule Policy
                    {showWritebackTooltip && (
                        <span style={{ marginLeft: 4, display: 'inline-flex' }}>
                            <VyneTooltip>
                                <span>
                                    This location cannot accept direct Cancellations or Reschedules because PMS
                                    Writeback is not enabled. To enable Writeback, please visit the{' '}
                                    <Link
                                        style={{ color: '#fff', textDecoration: 'underline' }}
                                        to={`/writeback-settings/${practiceId}`}
                                    >
                                        PMS Writeback Settings
                                    </Link>{' '}
                                    page.
                                </span>
                            </VyneTooltip>
                        </span>
                    )}
                </label>
                <VyneSelect
                    dataTestId="reschedule-policy-select"
                    value={configuration.reschedule_appointment}
                    onChange={(value: string) => onUpdate({ ...configuration, reschedule_appointment: value })}
                    style={{ width: '100%' }}
                    options={reschedulingPolicyOptions}
                    disabled={disabled}
                />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={2.3} xl={2.3} className={`${moduleName}__table-item details`}>
                <label className={`${moduleName}__input-label`}>Allow Reschedule Changes Unit</label>
                <VyneSelect
                    dataTestId="reschedule-prior-unit-select"
                    value={configuration.reschedule_prior_unit}
                    onChange={(value: 'days' | 'hours') =>
                        onUpdate({
                            ...configuration,
                            reschedule_prior_unit: value,
                            reschedule_prior: coercePriorValueForUnit(configuration.reschedule_prior, value),
                        })
                    }
                    style={{ width: '100%' }}
                    options={priorUnitOptions}
                    disabled={disabled}
                />
            </Grid>
            <Grid item={true} className={`${moduleName}__table-item details`} xs={6} sm={3} md={3} lg={2.3} xl={2.3}>
                <label className={`${moduleName}__input-label`}>Allow Reschedule Changes</label>
                <VyneSelect
                    dataTestId="reschedule-prior-select"
                    value={configuration.reschedule_prior.toString()}
                    onChange={(value: string) => onUpdate({ ...configuration, reschedule_prior: Number(value) })}
                    style={{ width: '100%' }}
                    options={priorOptions}
                    disabled={disabled}
                />
            </Grid>
            {isDentrixAgent ? (
                <Grid
                    item={true}
                    xs={4}
                    sm={3}
                    md={2}
                    lg={1}
                    className={`${moduleName}__table-item details ${moduleName}__checkbox`}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    <label className={`${moduleName}__input-label`} style={{ textAlign: 'center', width: '100%' }}>
                        Sidebooking
                    </label>
                    <VyneCheckbox
                        checked={configuration.concurrency_num === undefined || configuration.concurrency_num === 2}
                        onChange={(e: CheckboxChangeEvent) =>
                            onUpdate({ ...configuration, concurrency_num: e.target.checked ? 2 : 1 })
                        }
                        label=""
                        dataTestId="reschedule-sidebooking-checkbox"
                        style={{ marginBottom: 0, alignItems: 'center', marginLeft: '10px' }}
                        disabled={disabled}
                    />
                </Grid>
            ) : (
                <Grid
                    item={true}
                    className={`${moduleName}__table-item details`}
                    sm={3}
                    md={3}
                    lg={1}
                    style={{ borderTop: 'none' }}
                >
                    <label className={`${moduleName}__input-label`}>Sidebooking</label>
                    <VyneSelect
                        dataTestId="reschedule-sidebooking-select"
                        value={configuration.concurrency_num.toString()}
                        onChange={(value: string) => onUpdate({ ...configuration, concurrency_num: Number(value) })}
                        style={{ width: '100%', alignItems: 'center' }}
                        options={sidebookingOptions}
                        disabled={disabled}
                    />
                </Grid>
            )}
        </>
    )
}

export default ReschedulingFields
